/**
 * Security utility for sanitizing HTML content
 * Uses DOMPurify for safe HTML sanitization
 */

import DOMPurify from "dompurify";

// DOMPurify-based HTML sanitization
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html ?? "", {
    // Add tags needed for numerology explanations (div, section, article, headers)
    ADD_TAGS: ["div", "section", "article", "h1", "h2", "h3", "h4", "h5", "h6", "hr"],
    // Add class attribute for styling (style attribute is handled by DOMPurify's safe defaults)
    ADD_ATTR: ["class"],
  });
}

// Safe text content extraction (no HTML)
export function extractTextContent(html: string): string {
  if (!html) return '';
  
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
}

// Safe HTML rendering component for React
export function createSafeHtmlProps(html: string) {
  const sanitized = sanitizeHtml(html ?? "");
  return { dangerouslySetInnerHTML: { __html: sanitized } };
}

// Highlight repeated sequences safely without removing HTML formatting
export function highlightRepeatedSequencesSafe(text: string): string {
  if (!text) return '';
  
  // Sanitize dangerous content first
  const sanitizedText = sanitizeHtml(text);
  
  // Regex for 3+ identical digits separated by NBSP (entity or character)
  const NBSP = '(?:&nbsp;|\\u00A0)';
  const reSeq = new RegExp(`(\\d)(${NBSP}\\1){2,}`, 'g');
  
  const highlighted = sanitizedText.replace(reSeq, (match) => {
    return `<span class="numerology-highlight">${match}</span>`;
  });
  
  return highlighted;
}